/*
 *
 * Chatbot
 *
 * @version 1.0.0
 * @author Marco Alessandroni
 * @since 16-Apr-2025
 */
import React, { useEffect, useRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Capacitor } from '@capacitor/core'
import {
  getChatbotScriptBasedOnENV,
  openExternalUrl
} from './getChatbotScriptBasedOnENV'
import { observeChatbotGatewayActivity } from './observeChatbotGatewayActivity'

const Chatbot = ({ actions, isMounted }) => {
  // Returns the chatbot script URL and sets portalToken + logged.
  const scriptChatbotUrl = useMemo(
    () => getChatbotScriptBasedOnENV(actions.doSessionActivity),
    [actions.doSessionActivity]
  )
  const scriptRef = useRef(null)

  const callChatbotScript = useCallback(() => {
    const script = document.createElement('script')

    if (window.renderExternalComponent) {
      window.renderExternalComponent(
        () => { },
        null,
        () => actions.doChatBotMounted(false),
        openExternalUrl
      )
    } else {
      script.src = `${scriptChatbotUrl}?v=${Date.now()}`
      script.async = true
      script.charset = 'UTF-8'
      script.onload = () => {
        if (window.renderExternalComponent) {
          window.renderExternalComponent(
            () => { },
            null,
            () => actions.doChatBotMounted(false),
            openExternalUrl
          )
        }
      }
    }

    scriptRef.current = script
    document.body.appendChild(script)
    actions.doChatBotMounted(true)
  }, [scriptChatbotUrl, actions])

  useEffect(() => {
    /*
      1) Calculate and set "env" in sessionStorage
         based on the DEPLOY_ENV_NAME build variable.
    */
    const buildEnv = process.env.DEPLOY_ENV_NAME || process.env.ENVIRONMENT || 'DEVELOP'
    let env = 'DEV'

    if (buildEnv === 'PRODUCTION') {
      env = 'PROD'
    } else if (buildEnv === 'STAGING') {
      env = 'STG'
    } else {
      env = 'DEV'
    }

    window.sessionStorage.setItem('env', env)

    /*
      2) Set portalName and openLinkWithCordova
         based on the current platform.
    */
    if (Capacitor.isNativePlatform()) {
      window.sessionStorage.setItem('portalName', 'NEW_NEXI_BUSINESS_APP')
      window.sessionStorage.setItem(
        'openLinkWithCordova',
        (url) => openExternalUrl(url)
      )
    } else {
      window.sessionStorage.setItem('portalName', 'NEW_NEXI_BUSINESS_WEB')
    }

    /*
      3) If the chatbot is not mounted yet,
         load the script after 300ms.
    */
    if (!isMounted) {
      const timer = setTimeout(callChatbotScript, 300)
      return () => clearTimeout(timer)
    }
  }, [isMounted, callChatbotScript])

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      return undefined
    }

    return observeChatbotGatewayActivity(actions.doSessionActivity)
  }, [actions.doSessionActivity])

  // Cleanup on unmount.
  useEffect(
    () => () => {
      if (scriptRef.current) {
        document.body.removeChild(scriptRef.current)
      }
      if (Capacitor.isNativePlatform()) {
        setTimeout(() => actions.doChatBotMounted(false), 900)
      } else {
        actions.doChatBotMounted(false)
      }
    },
    [actions]
  )

  return <div id='chatbotApp' />
}

Chatbot.propTypes = {
  actions: PropTypes.shape({
    doChatBotMounted: PropTypes.func.isRequired,
    doSessionActivity: PropTypes.func.isRequired
  }).isRequired,
  isMounted: PropTypes.bool
}

Chatbot.defaultProps = {
  isMounted: false
}

export default Chatbot
